// Employee Authentication Types

// Role constants matching backend middleware/rbac.go
export type Role = 'ceo' | 'admin' | 'manager' | 'employee' | 'customer' | 'readonly';

// Permission constants matching backend middleware/rbac.go
export type Permission =
  // User management
  | 'user:create' | 'user:read' | 'user:update' | 'user:delete'
  // Purchase Orders
  | 'po:create' | 'po:read' | 'po:update' | 'po:approve' | 'po:delete'
  // Sales Orders
  | 'so:create' | 'so:read' | 'so:update' | 'so:approve' | 'so:delete'
  // Inventory
  | 'inventory:read' | 'inventory:update'
  // Vendors
  | 'vendor:read' | 'vendor:write'
  // Customers
  | 'customer:read' | 'customer:write'
  // BOM
  | 'bom:read' | 'bom:write'
  // Kanban
  | 'kanban:read' | 'kanban:write'
  // MES
  | 'mes:read' | 'mes:write'
  // TTQS
  | 'ttqs:read' | 'ttqs:write'
  // Financials
  | 'financial:read'
  // Audit
  | 'audit:read'
  // System
  | 'system:config';

// Role hierarchy levels (higher = more permissions)
export const ROLE_LEVELS: Record<Role, number> = {
  ceo: 5,
  admin: 4,
  manager: 3,
  employee: 2,
  customer: 1,
  readonly: 0,
};

// Internal roles (employees, not customers)
export const INTERNAL_ROLES: Role[] = ['ceo', 'admin', 'manager', 'employee'];

export interface IEmployee {
  name: string;
  username: string;
  base_salary: number;
  hourly_salary: number;
  overtime_pay: number;
  labor_insurance: number;
  healthcare_pension: number;
  role: Role;
}

export interface IEmployeePublic {
  name: string;
  username: string;
  base_salary: number;
  hourly_salary: number;
  overtime_pay: number;
  labor_insurance: number;
  healthcare_pension: number;
  role: Role;
}

export interface IEmployeeSalaryInfo {
  name: string;
  username: string;
  base_salary: number;
  hourly_salary: number;
  overtime_pay: number;
  labor_insurance: number;
  healthcare_pension: number;
  bank: string;
  bank_branch: string;
  account_last4: string;
}

export interface ILoginRequest {
  username: string;
  password: string;
}

export interface IGoogleLoginRequest {
  credential: string;
}

export interface IPasscodeVerifyRequest {
  passcode: string;
}

export interface IPasscodeVerifyResponse {
  verified: boolean;
}

export interface ILoginResponse {
  token: string;
  user: IEmployeePublic;
  role: Role;
  permissions: Permission[];
}

export interface ICurrentUserResponse {
  user: IEmployeePublic;
  role: Role;
  permissions: Permission[];
}

// Helper functions
export function hasPermission(permissions: Permission[], required: Permission): boolean {
  return permissions.includes(required);
}

export function hasAnyPermission(permissions: Permission[], required: Permission[]): boolean {
  return required.some(p => permissions.includes(p));
}

export function hasAllPermissions(permissions: Permission[], required: Permission[]): boolean {
  return required.every(p => permissions.includes(p));
}

export function canAccessRole(userRole: Role, requiredRole: Role): boolean {
  return ROLE_LEVELS[userRole] >= ROLE_LEVELS[requiredRole];
}

export function isInternalRole(role: Role): boolean {
  return INTERNAL_ROLES.includes(role);
}
